if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        this.initThumbnails();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initThumbnails() {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.elements.viewer.resetPages();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const headerOffset = this.getStickyHeaderOffset();
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the media top is already below the sticky header (incl. mobile exposed nav)
          if (activeMediaRect.top >= headerOffset - 0.5) return;
          const top = activeMediaRect.top + window.scrollY - headerOffset;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      getStickyHeaderOffset() {
        const header =
          document.querySelector('header-section.section-header') || document.querySelector('.section-header');
        if (!header) return 0;
        return header.getBoundingClientRect().bottom;
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      getVisualizerSlide() {
        return this.elements.viewer?.querySelector('[id$="-bundle-visualizer"]') || null;
      }

      getVisualizerSlideIndex() {
        const viewer = this.elements.viewer;
        const slide = this.getVisualizerSlide();
        if (!viewer || !slide) return -1;
        const items = viewer.sliderItemsToShow || [];
        return items.indexOf(slide);
      }

      getCurrentSlideIndex() {
        const viewer = this.elements.viewer;
        const slider = viewer?.slider;
        if (!slider || !viewer?.sliderItemOffset) return 0;
        return Math.round(slider.scrollLeft / viewer.sliderItemOffset);
      }

      isOnVisualizerSlide() {
        const visualizerIndex = this.getVisualizerSlideIndex();
        if (visualizerIndex < 0) return true;
        return this.getCurrentSlideIndex() === visualizerIndex;
      }

      cancelVisualizerSlideScroll() {
        if (this._visualizerScrollRaf) {
          cancelAnimationFrame(this._visualizerScrollRaf);
          this._visualizerScrollRaf = null;
        }
        if (this._visualizerScrollCleanup) {
          this._visualizerScrollCleanup();
          this._visualizerScrollCleanup = null;
        }
      }

      goToVisualizerSlide() {
        const viewer = this.elements.viewer;
        const slider = viewer?.slider;
        const visualizerIndex = this.getVisualizerSlideIndex();
        if (!slider || visualizerIndex < 0) return;

        viewer.initPages?.();

        const items = viewer.sliderItemsToShow || [];
        const targetLeft = items[visualizerIndex]?.offsetLeft ?? visualizerIndex * viewer.sliderItemOffset;
        if (Math.abs(slider.scrollLeft - targetLeft) < 2) return;

        this.cancelVisualizerSlideScroll();

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) {
          slider.scrollLeft = targetLeft;
          viewer.update?.();
          return;
        }

        const slideDistance = Math.abs(this.getCurrentSlideIndex() - visualizerIndex);
        const duration = Math.min(620, 450 + slideDistance * 95);

        const startLeft = slider.scrollLeft;
        const distance = targetLeft - startLeft;
        const startTime = performance.now();
        const snapClasses = ['snap-x', 'snap-mandatory'];
        const hadSnap = snapClasses.every((cls) => slider.classList.contains(cls));

        if (hadSnap) slider.classList.remove(...snapClasses);
        slider.style.scrollSnapType = 'none';

        this._visualizerScrollCleanup = () => {
          slider.style.scrollSnapType = '';
          if (hadSnap) snapClasses.forEach((cls) => slider.classList.add(cls));
          slider.scrollLeft = targetLeft;
          viewer.update?.();
        };

        // Gentle ease-in-out — soft start and settle, closer to native carousel feel.
        const easeInOut = (t) => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);

        const step = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          slider.scrollLeft = startLeft + distance * easeInOut(progress);
          if (progress < 1) {
            this._visualizerScrollRaf = requestAnimationFrame(step);
            return;
          }
          this.cancelVisualizerSlideScroll();
        };

        this._visualizerScrollRaf = requestAnimationFrame(step);
      }

      connectedCallback() {
        this.initBundleStickyGalleryPin();
      }

      disconnectedCallback() {
        this.cancelVisualizerSlideScroll();
        this.destroyBundleStickyGalleryPin();
      }

      // Requires bundle-sticky-gallery.js (defines BundleStickyGalleryPin and BV_MOBILE_MQ)
      // and data-bundle-sticky-gallery on <media-gallery> (global killswitch: settings.enable_bundle_sticky_gallery).
      initBundleStickyGalleryPin() {
        if (!this.hasAttribute('data-bundle-sticky-gallery') || !window.BundleStickyGalleryPin) return;

        const mobileMq = window.BV_MOBILE_MQ || '(max-width: 767px)';
        this.stickyPinMql = window.matchMedia(mobileMq);
        this.onStickyPinMqlChange = () => {
          if (this.stickyPinMql.matches) {
            if (!this.stickyGalleryPin) {
              this.stickyGalleryPin = new window.BundleStickyGalleryPin(this);
            }
          } else {
            this.stickyGalleryPin?.destroy();
            this.stickyGalleryPin = null;
          }
        };
        this.stickyPinMql.addEventListener('change', this.onStickyPinMqlChange);
        this.onStickyPinMqlChange();
      }

      destroyBundleStickyGalleryPin() {
        this.stickyPinMql?.removeEventListener('change', this.onStickyPinMqlChange);
        this.stickyGalleryPin?.destroy();
        this.stickyGalleryPin = null;
        this.stickyPinMql = null;
        this.onStickyPinMqlChange = null;
      }
    },
  );
}
